import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, ShieldCheck, HandCoins, HomeIcon, Sparkles, Phone } from "lucide-react";
import api from "@/lib/api";
import { SavingsCalculator } from "@/components/SavingsCalculator";
import { FaqSection } from "@/components/FaqSection";
import { LeadMagnet } from "@/components/LeadMagnet";
import { ReviewsSection } from "@/components/ReviewsSection";
import { usePageMeta } from "@/lib/seo";

const HERO_IMG = "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=1200&q=70";
const METHOD_IMG = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?auto=format&fit=crop&w=1200&q=70";
const FAMILY_IMG = "https://images.unsplash.com/photo-1728172490177-9c5c8f31da94?auto=format&fit=crop&w=1200&q=70";
const HANDSHAKE_IMG = "https://images.unsplash.com/photo-1745847768380-2caeadbb3b71?auto=format&fit=crop&w=1400&q=70";
const DELEGA_IMG = "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1000&q=70";
const STUDIO_IMG = "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1000&q=70";

const RevealLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 900;
  const heavy = !reduced && !isMobile;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, heavy ? 180 : 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, heavy ? 1.1 : 1]);

  return (
    <section ref={ref} className="relative min-h-[100svh] pt-28 md:pt-32 pb-20 overflow-hidden bg-[#F8FAFC]" data-testid="hero-section">
      {/* Soft warm ambient gradient — light background, no dark overlay */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFFFFF] via-[#F8FAFC] to-[#F1F5F9]"/>
        {/* Blue-tinted radial glow behind the copy */}
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#14417B]/10 blur-3xl"/>
        <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-[#14417B]/15 blur-3xl"/>
      </div>

      <div className="container-editorial relative">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left: copy */}
          <div className="col-span-12 md:col-span-7">
            <div className="mt-6">
              <RevealLine delay={0.15}>
                <h1
                  data-testid="hero-title"
                  className="inline-block font-serif font-extrabold tracking-tight leading-none text-5xl md:text-6xl text-[#2d6fc4]"
                  style={{ fontFamily: "'Montserrat', ui-sans-serif, sans-serif" }}
                >
                  Sdebitiamoci<span className="text-[#2d6fc4]">.</span>
                </h1>
              </RevealLine>
            </div>

            <div className="mask-line mt-6">
              <motion.p
                data-testid="hero-slogan"
                initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.5, ease: [0.22,1,0.36,1] }}
                className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#2d6fc4]"
              >
                Senza debiti, <span className="italic text-[#2d6fc4]">senza pensieri.</span>
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }}
              data-testid="hero-description"
              className="mt-8 max-w-xl space-y-3 text-base md:text-lg text-[#333333] leading-relaxed"
            >
              <p>
                Aiutiamo <span className="text-[#2d6fc4] font-medium">privati e aziende</span> a chiudere posizioni debitorie con banche, finanziarie, condominio e società di recupero, riducendo l'importo dovuto attraverso trattative reali di <span className="text-[#2d6fc4] font-medium">saldo e stralcio</span>.
              </p>
              <p className="text-[#333333]">
                La <span className="text-[#2d6fc4] font-semibold">consulenza è gratuita</span>. Paghi una <span className="text-[#2d6fc4] font-medium">percentuale solo al successo</span>, sul risparmio ottenuto. Niente anticipi, niente sorprese.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
              className="mt-8 flex flex-col sm:flex-row items-start gap-4"
            >
              <a
                href="tel:+393927715932"
                data-testid="hero-call-btn"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-lg text-base font-semibold tracking-wide text-white bg-[#16A34A] hover:bg-[#15803D] shadow-lg"
                style={{ transition: "background-color 200ms ease" }}
              >
                <Phone size={18}/> Chiama ora
              </a>
              <Link
                to="/contatti"
                data-testid="hero-cta-primary"
                className="btn-cta group inline-flex items-center gap-2 px-8 py-4 rounded-lg text-base font-semibold tracking-wide"
              >
                Richiedi consulenza gratuita
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/>
              </Link>
              <Link
                to="/metodo"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 text-sm text-[#2d6fc4] border border-[#0B1F3F]/20 hover:border-[#14417B] px-6 py-4 rounded-lg"
                style={{ transition: "border-color 200ms ease" }}
              >
                Scopri il metodo <ArrowRight size={14}/>
              </Link>
            </motion.div>

            <p className="mt-4 text-xs text-[#333333] font-mono tracking-wider">
              Risposta entro 24h · Nessun costo · Nessun impegno
            </p>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-lg border-t border-[#E5E7EB] pt-6"
            >
              <div>
                <p className="font-serif text-4xl text-[#2d6fc4]">0€</p>
                <p className="mt-1 text-xs text-[#333333]">anticipo iniziale</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-[#2d6fc4]">65%</p>
                <p className="mt-1 text-xs text-[#333333]">media riduzione</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-[#2d6fc4]">20<span className="text-2xl">+</span></p>
                <p className="mt-1 text-xs text-[#333333]">anni di esperienza</p>
              </div>
            </motion.div>
          </div>

          {/* Right: engaging image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4, duration: 1, ease: [0.22,1,0.36,1] }}
            className="col-span-12 md:col-span-5 relative"
          >
            <motion.div
              style={{ y: heavy ? y : 0, scale: heavy ? scale : 1 }}
              className="relative aspect-[4/5] w-full overflow-hidden rounded-lg shadow-2xl"
            >
              <img
                src={HERO_IMG}
                alt="Famiglia serena — obiettivo di Sdebitiamoci"
                fetchPriority="high"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Subtle blue tint on the image for brand coherence */}
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1F3F]/25 via-transparent to-[#14417B]/10 mix-blend-multiply"/>
            </motion.div>

            {/* Floating quote card */}
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
              className="hidden md:block absolute -bottom-6 -left-8 max-w-[260px] rounded-lg bg-white border border-[#E5E7EB] shadow-xl p-5"
            >
              <p className="font-serif text-lg text-[#2d6fc4] leading-tight">
                "La casa era all'asta. <span className="italic text-[#2d6fc4]">Oggi è ancora nostra.</span>"
              </p>
              <div className="mt-3 flex items-center justify-between border-t border-[#E5E7EB] pt-3 text-xs">
                <span className="text-[#333333]">Famiglia T. — Bergamo</span>
                <span className="font-serif text-2xl text-[#2d6fc4]">-62%</span>
              </div>
            </motion.div>

            {/* Trust chip top-right */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
              className="hidden md:flex absolute -top-4 -right-4 items-center gap-2 rounded-full bg-[#1a4a8a] text-white px-4 py-2 shadow-lg"
            >
              <span className="h-2 w-2 rounded-full bg-[#14417B] animate-pulse"/>
              <span className="text-[10px] font-mono tracking-[0.2em] uppercase">Operativi ora</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom scroll cue */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#333333]"
      >
        scorri ↓ il metodo
      </motion.div>
    </section>
  );
}

function MarqueeBand() {
  return (
    <div className="border-y border-[#E5E7EB] bg-[#FFFFFF] py-6 overflow-hidden">
      <Marquee gradient={false} speed={35} pauseOnHover>
        {[
          "IL METODO ONESTO",
          "SALDO E STRALCIO",
          "SALVIAMO LA TUA CASA",
          "ZERO ANTICIPI",
          "PAGHI SOLO AL SUCCESSO",
          "TRATTATIVE REALI",
          "PIÙ DI 20 ANNI DI ESPERIENZA",
        ].map((t) => (
          <span key={t} className="mx-10 font-serif text-5xl md:text-6xl text-[#2d6fc4] tracking-tight">
            {t} <span className="text-[#2d6fc4] mx-4">✻</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function TrustBanner() {
  return (
    <div className="bg-[#1a4a8a] text-white" data-testid="trust-banner">
      <div className="container-editorial py-6 md:py-8 flex flex-col md:flex-row items-center justify-center md:justify-between gap-4 text-center md:text-left">
        <p className="font-serif text-2xl md:text-3xl leading-tight">
          Oltre <span className="text-white">1.000 casi</span> risolti con successo.
        </p>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">
          Migliaia di pratiche gestite · Operativi in tutta Italia
        </p>
      </div>
    </div>
  );
}

function Manifesto() {
  const chapters = [
    { n: "01", t: "Consulenza gratuita", d: "Prima chiamata a costo zero. Raccontaci la tua situazione: importi, creditori, urgenze. Zero giudizio, zero pressione.", i: HANDSHAKE_IMG },
    { n: "02", t: "Delega e Protezione", d: "Con il mandato firmato prendiamo in carico ogni comunicazione con banche e servicer. Non parli più da solo con nessun creditore.", i: DELEGA_IMG },
    { n: "03", t: "Studio Tecnico", d: "Analisi documentale approfondita: prescrizioni, illegittimità, tassi usurari, conteggi. Costruiamo la mappa dei debiti reali.", i: STUDIO_IMG },
    { n: "04", t: "Strategia e Chiusura", d: "Trattiamo fino all'accordo firmato. Solo a saldo confermato — riceviamo la nostra percentuale sul risparmio.", i: FAMILY_IMG },
  ];
  return (
    <section className="py-24 md:py-32 bg-[#FFFFFF]" data-testid="manifesto-section">
      <div className="container-editorial">
        <div className="flex items-end justify-between mb-16 md:mb-24">
          <div className="max-w-2xl">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Il Metodo</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] mt-4 text-[#2d6fc4]">
              Quattro passi, <br/><em className="italic text-[#2d6fc4]">una promessa.</em>
            </h2>
          </div>
          <Link to="/metodo" className="hidden md:inline-flex items-center gap-2 text-sm link-underline pb-0.5">
            Vedi tutto il processo <ArrowRight size={14}/>
          </Link>
        </div>

        <div className="space-y-24 md:space-y-32">
          {chapters.map((c, idx) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`grid grid-cols-12 gap-6 items-center ${idx % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="col-span-12 md:col-span-6 relative">
                <div className="aspect-[4/5] overflow-hidden rounded-lg clip-square">
                  <img loading="lazy" decoding="async" src={c.i} alt={c.t} className="w-full h-full object-cover" />
                </div>
                <span className="chapter-num absolute -top-8 -left-4 md:-left-8 text-[12rem] md:text-[16rem] text-[#2d6fc4]/15 pointer-events-none select-none">{c.n}</span>
              </div>
              <div className="col-span-12 md:col-span-6 md:pl-16">
                <p className="chapter-num text-[#2d6fc4] text-6xl">{c.n}</p>
                <h3 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-[#2d6fc4]">{c.t}</h3>
                <p className="mt-6 text-lg text-[#333333] leading-relaxed max-w-md">{c.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustPillars() {
  const pillars = [
    { icon: HandCoins, t: "Zero anticipo", d: "Nessun costo iniziale. Ti seguiamo interamente prima di ricevere un euro." },
    { icon: ShieldCheck, t: "Paghi al successo", d: "Una percentuale — solo sul risparmio ottenuto e a saldo firmato." },
    { icon: HomeIcon, t: "Case salvate", d: "Fermiamo pignoramenti e aste. La tua casa resta la tua casa." },
    { icon: Sparkles, t: "Metodo onesto", d: "Nessuna promessa da bar. Ogni pratica analizzata singolarmente." },
  ];
  return (
    <section className="py-24 md:py-28 bg-[#1a4a8a] text-[#FFFFFF]" data-testid="pillars-section">
      <div className="container-editorial">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-white">— Perché noi</p>
            <h2 className="font-serif text-5xl md:text-6xl mt-4 leading-[0.95]">
              Quattro impegni.<br/><em className="italic text-white">Zero eccezioni.</em>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {pillars.map((p, i) => (
              <motion.div
                key={p.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="border border-white/10 rounded-lg p-6 hover:border-[#CBD5E1]/40 hover:bg-white/[0.02]"
                style={{ transition: "border-color 300ms ease, background-color 300ms ease" }}
              >
                <p.icon size={22} strokeWidth={1.5} className="text-white"/>
                <h3 className="font-serif text-2xl mt-4">{p.t}</h3>
                <p className="mt-2 text-sm text-[#FFFFFF]/70 leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(r => setItems(r.data)).catch(()=>{});
  }, []);
  return (
    <section className="py-24 md:py-32 bg-[#F1F5F9]" data-testid="testimonials-section">
      <div className="container-editorial">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Casi reali</p>
            <h2 className="font-serif text-5xl md:text-6xl mt-4 leading-[0.95] text-[#2d6fc4]">
              Case salvate,<br/><em className="italic text-[#2d6fc4]">vite ricomposte.</em>
            </h2>
          </div>
          <Link to="/casi" className="hidden md:inline-flex items-center gap-2 text-sm link-underline pb-0.5">
            Tutti i casi <ArrowRight size={14}/>
          </Link>
        </div>
        <div className="grid grid-cols-12 gap-6">
          {items.slice(0, 4).map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className={`case-card border border-[#E5E7EB] bg-[#FFFFFF] rounded-lg p-8 ${i === 0 ? "col-span-12 md:col-span-7 md:row-span-2" : "col-span-12 md:col-span-5"}`}
              data-testid={`testimonial-${i}`}
            >
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">— {c.role}</p>
              <p className={`mt-6 font-serif text-[#2d6fc4] leading-[1.15] ${i === 0 ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
                “{c.quote}”
              </p>
              <div className="mt-8 flex items-end justify-between border-t border-[#E5E7EB] pt-4">
                <div>
                  <p className="font-medium text-[#2d6fc4]">{c.name}</p>
                  <p className="text-xs text-[#333333] mt-1">Debito da {c.debt_before} → {c.debt_after}</p>
                </div>
                <p className="font-serif text-4xl text-[#2d6fc4]">-{c.saving_pct}%</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative py-24 md:py-36 overflow-hidden bg-[#1a4a8a]" data-testid="final-cta">
      <div className="absolute inset-0 -z-10">
        <img loading="lazy" decoding="async" src={HANDSHAKE_IMG} className="w-full h-full object-cover opacity-70" alt="" onError={(e)=>{e.currentTarget.style.display='none';}}/>
        <div className="absolute inset-0 bg-[#1a4a8a]/70"/>
      </div>
      <div className="container-editorial text-[#FFFFFF]">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-white">— Il primo passo</p>
        <h2 className="font-serif text-4xl md:text-6xl lg:text-8xl leading-[1.02] mt-6 max-w-4xl">
          <span className="block">Consulenza conoscitiva.</span>
          <em className="not-italic block mt-2">
            <span className="italic text-white">Contattaci senza impegno.</span>
          </em>
        </h2>
        <p className="mt-8 max-w-xl text-lg text-[#FFFFFF]/80">
          Ti risponderemo entro 24 ore. Prima capiamo la tua situazione, poi decidiamo insieme se possiamo aiutarti.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/contatti" data-testid="final-cta-form" className="btn-cta inline-flex items-center gap-2 px-7 py-4 rounded-lg text-sm tracking-wide">
            Richiedi consulenza <ArrowUpRight size={16}/>
          </Link>
          <a href="https://wa.me/393927715932" target="_blank" rel="noreferrer" data-testid="final-cta-whatsapp" className="border border-white/40 hover:bg-white/10 px-7 py-4 rounded-lg text-sm tracking-wide inline-flex items-center gap-2" style={{transition:"background-color 250ms ease"}}>
            Scrivi su WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta(
    "Sdebitiamoci Srl · Saldo e Stralcio — Riduci i debiti dal 50 al 70%",
    "Riduciamo i debiti di privati e aziende dal 50% al 70% con il saldo e stralcio. Consulenza gratuita, paghi solo al successo. Calcola subito il tuo risparmio."
  );
  return (
    <div data-testid="home-page">
      <Hero />
      <TrustBanner />
      <MarqueeBand />
      <Manifesto />
      <TrustPillars />
      <SavingsCalculator />
      <Testimonials />
      <ReviewsSection />
      <FaqSection />
      <LeadMagnet />
      <FinalCTA />
    </div>
  );
}
